import React from "react";
import "./SharedStyles.scss";
import { IoMdAlert, IoMdCheckmarkCircle, IoMdWarning } from "react-icons/io";

const DEV_MESSAGE_PATTERNS = [
  /Can not instantiate/i,
  /\[simple type, class /,
  /at \[Source:/,
  /com\.\w+\.\w+\.\w+/,
  /\n\s*at /,
  /java\.(lang|util|io|net)\./,
  /org\.\w+\.\w+\.\w+/,
];

const isDevMessage = (msg: string): boolean => DEV_MESSAGE_PATTERNS.some((pattern) => pattern.test(msg));

const filterDevContent = (message: string): string => {
  if (!message) return message;
  return message
    .split(", ")
    .filter((part) => !isDevMessage(part))
    .join(", ")
    .trim();
};

interface IProps {
  message?: string;
  messages?: string[];
  type: "error" | "warning" | "success" | "gray";
  showOnlyIcon?: boolean;
  compact?: boolean;
  variant?: "default" | "chip";
  dangerouslySetInnerHTML?: boolean;
  buttonText?: string;
  onButtonClick?: () => void;
}

const EAAlert: React.FC<IProps> = ({
  message,
  messages,
  type,
  showOnlyIcon = false,
  compact = false,
  variant = "default",
  dangerouslySetInnerHTML = false,
  buttonText,
  onButtonClick,
}) => {
  const getIcon = (type: "success" | "error" | "warning" | "gray") => {
    switch (type) {
      case "success":
        return <IoMdCheckmarkCircle />;
      case "error":
        return <IoMdAlert />;
      case "warning":
        return <IoMdWarning />;
      case "gray":
        return <IoMdCheckmarkCircle />;
      default:
        return null;
    }
  };

  const icon = getIcon(type);

  const cleanMessages: string[] = messages
    ? messages.filter((msg) => !isDevMessage(msg))
    : [filterDevContent(message ?? "")].filter(Boolean);

  const alertClasses = `ea-alert ea-alert--${type} ${showOnlyIcon ? "ea-alert__only-icon" : ""} ${
    compact ? "ea-alert--compact" : ""
  } ${variant === "chip" ? "ea-alert--chip" : ""}`.trim();

  if (showOnlyIcon || cleanMessages.length === 0) {
    return (
      <div className={alertClasses}>
        <span className="ea-alert__icon">{icon}</span>
      </div>
    );
  }

  return (
    <div className={alertClasses}>
      <span className="ea-alert__icon">{icon}</span>
      {dangerouslySetInnerHTML ? (
        <span className="ea-alert__message" dangerouslySetInnerHTML={{ __html: cleanMessages.join(" ") }} />
      ) : (
        <span className="ea-alert__message">
          {cleanMessages.length === 1 ? (
            cleanMessages[0]
          ) : (
            <span className="ea-alert__message-list">
              {cleanMessages.map((msg, i) => (
                <span key={i} className="ea-alert__message-item">
                  {msg}
                </span>
              ))}
            </span>
          )}
          {buttonText && onButtonClick && (
            <span onClick={onButtonClick} className={"ea-alert__button"}>
              {buttonText}
            </span>
          )}
        </span>
      )}
    </div>
  );
};

export default EAAlert;
